import { styled, TableCell, tableCellClasses, TableRow } from "@mui/material";
import dayjs from "dayjs";

//Custom Variables
export class DateTime {
    constructor(value?: any) {
		if (value) {
			if (value.toString().length === 10 && value.toString().includes('-')) {
				let dateParts = value.split('-');
				this.Value = new Date(dateParts[0], parseInt(dateParts[1]) - 1, dateParts[2], 0, 0, 0, 0);
			} else {
				this.Value = new Date(value);
			}
        } else {
            this.Value = new Date();
		}
    }
    Value: Date;
    toString() {
        return this.Value.toLocaleDateString();
    }
	toQueryString() {
		return dayjs(this.Value).format('YYYY-MM-DD');
	}
    toJSON() {
		return [
			this.Value.getFullYear(),
			(this.Value.getMonth() + 1).toString().padStart(2, '0'),
			this.Value.getDate().toString().padStart(2, '0'),
		].join('-');
    }
    valueOf() {
        return this.Value.getTime();
    }
    addDays(value: number) {
        return new DateTime(dayjs(this.Value).add(value, 'day').toDate());
    }
    addMonths(value: number) {
        return new DateTime(dayjs(this.Value).add(value, 'month').toDate());
    }
    diff(item: DateTime) {
        return dayjs(this.Value).diff(dayjs(item.Value));
    }
    dayDiff(item: DateTime) {
        return dayjs(this.Value).diff(dayjs(item.Value), 'day');
    }
    monthDiff(item: DateTime) {
        return dayjs(this.Value).diff(dayjs(item.Value), 'month');
	}
	startOfDay() {
		return new DateTime(dayjs(this.Value).startOf('day').toDate());
	}
	startOfMonth() {
		return new DateTime(dayjs(this.Value).startOf('month').toDate());
	}
	startOfYear() {
		return new DateTime(dayjs(this.Value).startOf('year').toDate());
	}
}

export interface SelectableItem {
	Id: number,
	Value: string
}

export interface SelectableItem2 {
	Id: string,
	Value: string
}

export interface LocalData {
	ShowNeedsEdit: boolean,
	ShowHasTaxes: boolean,
	ShowRepeating: boolean,
	ShowNeedsReimbursement: boolean
}

export interface KeyValuePair {
	Key: any,
	Value: any
}

export interface Grouping {
	Key: any,
	Value: any[]
}

//Account
export interface AccountItem {
	accountId: number,
	accountTypeId: number,
	description: string,
	amount: number,
	isDefault: boolean,
	autoAdjust: boolean,
	financialInstitution?: string,
	mask?: string,
	differentiator?: number,
	userAccountId: number
}

//Budget
export enum BudgetType {
	Expense = 0,
	Income = 1,
	Combined = 2
}

export interface SubBudgetVariant {
	subBudgetVariantId: number,
	subBudgetId: number,
	budgetVariantId: number,
	amount: number,
	incomeAmount: number,
	createdBy: number,
	createdDate: DateTime,
	modifiedBy?: number,
	modifiedDate?: DateTime
}

export interface SubBudgetItem {
	subBudgetId: number,
	budgetId: number,
	description: string,
	isInactive: boolean,
	variant?: SubBudgetVariant;
	amountSpent?: number,
	amountRemaining: number,
	incomeAmountGained?: number,
	incomeAmountRemaining?: number,
}

export interface BudgetItem {
	budgetId: number,
	timeframeId: number,
	description: string,
	color: string,
	bucketNodes: BudgetBucketNodeItem[],
	userIds: number[],
	budgetType: BudgetType,
	isMine: boolean,
	isViewTransactions: boolean,
	isInactive: boolean,
	requiresApproval: boolean,
	variant: BudgetVariant,
	amountSpent: number,
	amountRemaining: number,
	incomeAmountGained?: number,
	incomeAmountRemaining?: number,
	endDate?: DateTime,
	activeTimeframes: IdStartEnd[],
	unapprovedTransactions?: TransactionItem[],
	subBudgets?: SubBudgetItem[];
}
export interface BudgetVariant {
	budgetVariantId: number,
	budgetId: number,
	amount: number,
	incomeAmount: number,
	startDate: DateTime,
	endDate: DateTime | null,
	defaultEndDate: DateTime,
	isDefault: boolean,
	isInactive: boolean,
	timeframeId?: number,
	timeframe?: TimeframeItem,
	isFinal?: boolean
}

export interface IdStartEnd {
	id: number,
	startDate?: DateTime | null,
	endDate?: DateTime | null,
	timeframe?: TimeframeItem,
}

export interface BudgetBucketNodeItem {
	budgetBucketNodeId: number,
	bucketNodeId: number,
	bucketTypeKey: string,
	displayName: string,
	isInactive: boolean,
	isViewTransactions: boolean,
	startDate?: DateTime | null,
	endDate?: DateTime | null,
}

//Bucket Definitions
export interface BucketTypeDefinitionItem {
	bucketTypeKey: string,
	label: string,
	pluralLabel: string,
	parentBucketTypeKey?: string | null,
	isIndependent: boolean,
	isEnabled: boolean,
	requiresBudgetMapping: boolean,
	minSelectionsPerTransaction: number,
	maxSelectionsPerTransaction?: number | null,
	sortOrder: number
}

export interface BucketDefinitionConfigurationResponse {
	familyId: number,
	familyTypeId: number,
	isOverride: boolean,
	definitions: BucketTypeDefinitionItem[]
}

export interface BucketDefinitionSaveRequest {
	definitions: BucketTypeDefinitionItem[]
}

export enum BucketNodeRelationType {
	Hierarchy = 0,
	Binding = 1
}

export interface BucketNodeItem {
	bucketNodeId: number,
	bucketTypeKey: string,
	displayName: string,
	isInactive: boolean,
	isViewTransactions: boolean,
}

export interface BucketNodeRelationItem {
	bucketNodeRelationId: number,
	sourceBucketNodeId: number,
	targetBucketNodeId: number,
	relationType: BucketNodeRelationType
}

export interface BucketNodeSnapshotResponse {
	familyId: number,
	nodeCount: number,
	relationCount: number,
	nodes: BucketNodeItem[],
	relations: BucketNodeRelationItem[]
}

export interface BucketNodeTreeSaveNode {
	bucketNodeId?: number | null,
	bucketTypeKey: string,
	displayName: string,
	isInactive: boolean,
	isViewTransactions: boolean,
	children: BucketNodeTreeSaveNode[]
}

export interface BucketNodeTreeSaveRequest {
	rootBucketTypeKey: string,
	nodes: BucketNodeTreeSaveNode[]
}

export interface BucketNodeUpsertRequest {
	bucketNodeId?: number,
	bucketTypeKey: string,
	displayName: string,
	isInactive: boolean,
	isViewTransactions: boolean,
	parentBucketNodeId?: number | null
}

//Current User
export interface CurrentUser {
	email?: string,
	error?: string,
	isActive?: boolean,
	isAuthenticated?: boolean,
	isHoH?: boolean,
	mileageRate: number,
	familyName: string,
	token?: string,
	unassignedBudgets: boolean,
	includeAllUsersInBudgets: boolean,
	emails?: UserEmailItem[],
	bucketDefinitions?: BucketDefinitionConfigurationResponse,
	features?: FamilyTypeFeature[],
	families?: Family[]
}
export interface FamilyTypeFeature {
	featureId: number,
	value: string
}
export interface UserEmailItem {
	userEmailId: number,
	email: string,
	isVerified: boolean,
	isDuplicate: boolean
}

//Familiy
export interface Family {
	familyId: number,
	familyName: string,
	familyTypeId: number
}

//Family Code
export interface FamilyCode {
	familyCodeId: number,
	code: string,
	isAdmin: boolean
}

//Quick Receipt
export interface ReceiptEmailItem {
	receiptEmailId: number,
	userEmailId: number,
	familyId?: number | null,
	emailSubject: string,
	hasAttachment: boolean,
	receipts?: string[] | undefined,
	receiptEmailIds?: number[]
}

//Reports
export interface BudgetTransaction {
	Budget: BudgetItem,
	SubBudget: SubBudgetItem,
	Buckets: { [bucketTypeKey: string]: BucketNodeItem[] },
    User?: UserItem,
	Transaction: TransactionItem,
	Amount: TransactionAllocation
	Date: string,
	Merchant: string
}
export interface CustomReportSettings {
	title: string,
	grouping: string,
	columns: string[],
	columnLabels?: { [key: string]: string },
	filter: CustomReportFilter
}
export interface CustomReportFilter {
	startDate?: DateTime,
	endDate?: DateTime,
	createdById?: number[],
	nameIncludes?: string,
	merchantIncludes?: string,
	descriptionIncludes?: string,
	budgetIds?: number[],
	bucketNodeIds?: { [bucketTypeKey: string]: number[] },
	minAmount?: number,
	maxAmount?: number,
	includeSpending: boolean,
	includeMiles: boolean,
	isSeparateReportForMiles: boolean,
	includeCheckRequests: boolean,
	combineSplitBudgets: boolean,
	ignoreCombinedIfAnyNotIncluded: boolean,
	includeReceipts: boolean,
	groupReceipts: boolean
}

//Timeframe
export interface TimeframeItem {
    timeframeId: number,
    days: number,
    months: number,
    description: string,
	startDate: DateTime,
	endDate1?: DateTime | null,
	endDate2?: DateTime | null,
	longDescription: string
}

//Transaction
export interface TransactionItem {
    accountId: number,
    accountTransactionId: number,
	allocations: TransactionAllocation[],
	checkRequest?: TransactionCheckRequestItem,
	color: string,
	completedReimbursement: boolean,
	created: string,
	editedReceipt?: boolean,
	hasReceipt: boolean,
    isAdminReview?: boolean | null,
	isCheckRequest: boolean,
	isExternal?: boolean,
	isIncome?: boolean,
	isMiles: boolean,
	isMine: boolean,
	lastUpdated: string,
    merchant: string,
	miles?: number | null,
	needsMyApproval: boolean,
	needsReimbursement: boolean,
	rate?: number | null,
	receipts: string[],
    transactionDate: DateTime,
	receiptEmailIds?: number[],
	importedTransactionId?: number
}
export interface TransactionImage {
	accountTransactionId: number,
	image: HTMLImageElement,
	width: number,
	height: number
}
export interface TransactionAllocation {
	amount: number,
	approvalNote: string,
	budgetId: number,
	subBudgetId?: number,
	bucketNodes: BucketNodeItem[],
	details?: string,
	isApproved: boolean,
	isRejected: boolean,
	reviewerId: number | null
}
export interface TransactionCheckRequestItem {
	accountTransactionCheckRequestId: number,
	amount: number,
	reviewerId: number,
	dateNeeded: DateTime,
	needsMailed: boolean,
	mailingAddress?: string,
	city?: string,
	stateName?: string,
	zipCode?: string,
	approvalState: number,
	approvalNote?: string,
	needsMyApproval: boolean,
	isMine: boolean,
	created: string,
	reviewer?: string,
	createdDate: DateTime
}

//Transaction Filter
export interface AccountTransactionFilterModel {
	AccountId: number | undefined,
	NeedsReceipt: boolean,
	NeedsReimbursement: boolean,
	IsAdminReview: boolean,
	IsMine: boolean,
	IsRejected: boolean,
	Merchant: string,
	User: string,
	StartDate: Date | undefined,
	EndDate: Date | undefined,
	IncludeAccountIds: number[],
	IncludeBudgetIds: number[],
	ExcludeBudgetIds: number[],
	//IncludeCategoryIds: number[],
	//ExcludeCategoryIds: number[],
	MinAmount: number | undefined,
	MaxAmount: number | undefined
}

//User
export interface UserItem {
	userAccountId: number,
	familyId: number,
	firstName: string,
	lastName: string,
	isAdmin: boolean,
	canApproveChecks: boolean,
	isActive: boolean,
	isMe: boolean,
	lastLogin?: DateTime,
	createDate?: DateTime,
	families?: Family[],
	budgetIds?: number[]
}

//Functions
export const FamilyHasAccess = (user: CurrentUser, feature: number) => {
	return (user.features?.filter(f => f.featureId === feature).length ?? 0) > 0;
}

export const FamilyFeatureValue = (user: CurrentUser, feature: number) => {
	return user.features?.find(f => f.featureId === feature)?.value;
}

export const FamilyBucketLabel = (user: CurrentUser, bucketTypeKey: string, fallback: string) => {
	const label = user.bucketDefinitions?.definitions?.find(d => d.bucketTypeKey?.toLowerCase() === bucketTypeKey.toLowerCase())?.label?.trim();
	return label || fallback;
}

export const FamilyBucketPluralLabel = (user: CurrentUser, bucketTypeKey: string, fallback: string) => {
	const label = user.bucketDefinitions?.definitions?.find(d => d.bucketTypeKey?.toLowerCase() === bucketTypeKey.toLowerCase())?.pluralLabel?.trim();
	return label || fallback;
}

export const GetEnabledBucketDefinitions = (user: CurrentUser) => {
	return (user.bucketDefinitions?.definitions ?? [])
		.filter(definition => definition.isEnabled)
		.sort((first, second) => first.sortOrder - second.sortOrder);
}

export const IsActiveBudgetBucketNode = (node: { startDate?: DateTime | null, endDate?: DateTime | null }, date: DateTime) => {
	if (node.startDate && node.startDate.startOfDay() > date) return false;
	if (node.endDate && node.endDate.addDays(1).startOfDay() <= date) return false;
	return true;
}

export const GetBudgetBucketNodeOptions = (budget: BudgetItem | undefined, bucketTypeKey: string, date: DateTime) => {
	return [...(budget?.bucketNodes ?? [])]
		.filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase() && IsActiveBudgetBucketNode(node, date))
		.sort((first, second) => first.displayName.localeCompare(second.displayName));
}

export const GetAllocationBucketNodes = (allocation: TransactionAllocation | undefined, bucketTypeKey: string) => {
	return [...(allocation?.bucketNodes ?? [])]
		.filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase())
		.sort((first, second) => first.displayName.localeCompare(second.displayName));
}

export const GetAllocationBucketNodeIds = (allocation: TransactionAllocation | undefined, bucketTypeKey: string) => {
	return GetAllocationBucketNodes(allocation, bucketTypeKey)
		.map(node => node.bucketNodeId)
		.sort((first, second) => first - second);
}

export const SortBucketNodesHierarchically = <T extends { bucketNodeId: number, displayName: string }>(
	nodes: T[],
	relations: BucketNodeRelationItem[] | undefined
): T[] => {
	if (!nodes.length) return nodes;
	const nodeById = new Map<number, T>();
	nodes.forEach(node => nodeById.set(node.bucketNodeId, node));
	const parentByChild = new Map<number, number>();
	const childrenByParent = new Map<number, number[]>();
	(relations ?? [])
		.filter(relation => relation.relationType === BucketNodeRelationType.Hierarchy)
		.forEach(relation => {
			parentByChild.set(relation.targetBucketNodeId, relation.sourceBucketNodeId);
			const siblings = childrenByParent.get(relation.sourceBucketNodeId) ?? [];
			siblings.push(relation.targetBucketNodeId);
			childrenByParent.set(relation.sourceBucketNodeId, siblings);
		});
	const sortByName = (items: T[]) => [...items].sort((a, b) => a.displayName.localeCompare(b.displayName));
	const roots = sortByName(nodes.filter(node => {
		const parentId = parentByChild.get(node.bucketNodeId);
		return parentId === undefined || !nodeById.has(parentId);
	}));
	const result: T[] = [];
	const visited = new Set<number>();
	const visit = (node: T) => {
		if (visited.has(node.bucketNodeId)) return;
		visited.add(node.bucketNodeId);
		result.push(node);
		const childIds = childrenByParent.get(node.bucketNodeId) ?? [];
		const children = childIds
			.map(id => nodeById.get(id))
			.filter((child): child is T => !!child);
		sortByName(children).forEach(visit);
	};
	roots.forEach(visit);
	nodes.forEach(node => { if (!visited.has(node.bucketNodeId)) visit(node); });
	return result;
};

export const GetAvailableBucketNodeOptions = (
	snapshot: BucketNodeSnapshotResponse | undefined,
	budget: BudgetItem | undefined,
	definition: BucketTypeDefinitionItem,
	date: DateTime,
	allocation?: TransactionAllocation,
	extraOptions: Array<BucketNodeItem | BudgetBucketNodeItem> = []) => {
	const optionsById = new Map<number, BucketNodeItem | BudgetBucketNodeItem>();
	const addOption = (node: BucketNodeItem | BudgetBucketNodeItem | undefined) => {
		if (!node || optionsById.has(node.bucketNodeId)) return;
		optionsById.set(node.bucketNodeId, node);
	};

	if (definition.requiresBudgetMapping) {
		GetBudgetBucketNodeOptions(budget, definition.bucketTypeKey, date).forEach(addOption);
	} else {
		(snapshot?.nodes ?? [])
			.filter(node => node.bucketTypeKey.toLowerCase() === definition.bucketTypeKey.toLowerCase() && !node.isInactive)
			.forEach(addOption);
	}

	extraOptions
		.filter(node => node.bucketTypeKey.toLowerCase() === definition.bucketTypeKey.toLowerCase())
		.forEach(addOption);

	GetAllocationBucketNodes(allocation, definition.bucketTypeKey).forEach(addOption);

	if (definition.parentBucketTypeKey) {
		const parentTypeKey = definition.parentBucketTypeKey.toLowerCase();
		const selectedParentIds = new Set(
			(allocation?.bucketNodes ?? [])
				.filter(node => node.bucketTypeKey.toLowerCase() === parentTypeKey)
				.map(node => node.bucketNodeId));
		const parentByChild = new Map<number, number>();
		(snapshot?.relations ?? [])
			.filter(relation => relation.relationType === BucketNodeRelationType.Hierarchy)
			.forEach(relation => parentByChild.set(relation.targetBucketNodeId, relation.sourceBucketNodeId));
		Array.from(optionsById.keys()).forEach(id => {
			const parentId = parentByChild.get(id);
			if (parentId === undefined || !selectedParentIds.has(parentId)) {
				optionsById.delete(id);
			}
		});
	}

	const options: Array<BucketNodeItem | BudgetBucketNodeItem> = [];
	optionsById.forEach(option => options.push(option));

	return SortBucketNodesHierarchically(options, snapshot?.relations);
}

export const IsAllocationBucketSelectionValid = (allocation: TransactionAllocation | undefined, definition: BucketTypeDefinitionItem) => {
	const selectedCount = GetAllocationBucketNodeIds(allocation, definition.bucketTypeKey).length;
	if (selectedCount < definition.minSelectionsPerTransaction) return false;
	if (definition.maxSelectionsPerTransaction != null && selectedCount > definition.maxSelectionsPerTransaction) return false;
	return true;
}

export const RevalidateAllocationBucketNodes = (
	allocation: TransactionAllocation,
	budget: BudgetItem | undefined,
	definitions: BucketTypeDefinitionItem[],
	snapshot: BucketNodeSnapshotResponse | undefined,
	date: DateTime
) => {
	let next = allocation;
	definitions.forEach(definition => {
		const options = GetAvailableBucketNodeOptions(snapshot, budget, definition, date, next);
		const validOptionIds = new Set(options.map(option => option.bucketNodeId));
		let bucketNodeIds = GetAllocationBucketNodeIds(next, definition.bucketTypeKey)
			.filter(id => validOptionIds.has(id));
		if (definition.maxSelectionsPerTransaction != null) {
			bucketNodeIds = bucketNodeIds.slice(0, definition.maxSelectionsPerTransaction);
		}
		next = UpdateAllocationBucketNodeIds(next, definition.bucketTypeKey, bucketNodeIds, options);
	});
	return next;
}

export const UpdateAllocationBucketNodeIds = (allocation: TransactionAllocation, bucketTypeKey: string, bucketNodeIds: number[], options: Array<BucketNodeItem | BudgetBucketNodeItem>) => {
	const otherBucketNodes = (allocation.bucketNodes ?? []).filter(node => node.bucketTypeKey.toLowerCase() !== bucketTypeKey.toLowerCase());
	const optionById = options.reduce((result: Record<number, BucketNodeItem | BudgetBucketNodeItem>, option) => {
		result[option.bucketNodeId] = option;
		return result;
	}, {} as Record<number, BucketNodeItem | BudgetBucketNodeItem>);
	const nextBucketNodes = bucketNodeIds
		.map(id => optionById[id])
		.filter((node): node is BucketNodeItem | BudgetBucketNodeItem => !!node)
		.map(node => ({
			bucketNodeId: node.bucketNodeId,
			bucketTypeKey: node.bucketTypeKey,
			displayName: node.displayName,
			isInactive: node.isInactive,
			isViewTransactions: node.isViewTransactions,
		}) as BucketNodeItem);

	return {
		...allocation,
		bucketNodes: [...otherBucketNodes, ...nextBucketNodes]
	};
}

export const GetAllocationBucketLabel = (allocation: TransactionAllocation, definitions?: BucketTypeDefinitionItem[]) => {
	const sortOrderFor = (bucketTypeKey: string) => {
		const def = definitions?.find(d => d.bucketTypeKey?.toLowerCase() === bucketTypeKey?.toLowerCase());
		return def ? def.sortOrder : Number.MAX_SAFE_INTEGER;
	};
	const labels = [...(allocation.bucketNodes ?? [])]
		.sort((a, b) => sortOrderFor(a.bucketTypeKey) - sortOrderFor(b.bucketTypeKey))
		.map(node => node.displayName)
		.filter((label, index, values) => !!label && values.indexOf(label) === index);

	return labels.length > 0 ? labels.join(', ') : '';
}

export const BuildReportColumnLabels = (user: CurrentUser, columns: string[]) => {
	return columns.reduce((result: { [key: string]: string }, column: string) => {
		if (column && column.toLowerCase().startsWith('bucket:')) {
			const bucketTypeKey = column.substring('bucket:'.length);
			result[column] = FamilyBucketLabel(user, bucketTypeKey, bucketTypeKey);
		} else {
			result[column] = FamilyBucketLabel(user, column, column);
		}
		return result;
	}, {});
}

export const GroupBy: (list: any[], key: string) => Grouping[] = (list: any[], key: string) => {
	return list.reduce((result: Grouping[], item: any) => {
		let res = result.find(r => r.Key === item[key]);
		if (res) {
			res.Value.push(item);
		}
		else {
			res = {
				Key: item[key],
				Value: [item]
			};
			result.push(res);
		}
		return result;
	}, []);
}

export const USCurrency: (value: number) => string = (value: number) =>
	value.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD'
	});

export const RoundToCent: (value: number) => number = (value: number) =>
	Math.round(value * 100) / 100;

export const BudgetTransactionTotal: (group: BudgetTransaction[]) => number = (group: BudgetTransaction[]) =>
	group.reduce((total: number, b: BudgetTransaction) => b.Amount.amount + total, 0);

export const GroupTransactionTotal: (items: Grouping[]) => number = (items: Grouping[]) =>
	items.reduce(
		(total: number, g: Grouping) =>
			BudgetTransactionTotal(g.Value as BudgetTransaction[]) + total, 0);

export const DictToSelectable = (item: { [index: number]: string }) => {
	let keys = Object.keys(item);
	let result: SelectableItem[] = [];
	for (let id of keys) {
		result.push({ Id: parseInt(id), Value: item[parseInt(id)] })
	}
	return result;
}

export const TimeframeDays: (timeframe: TimeframeItem | undefined) => number = (timeframe: TimeframeItem | undefined) => {
	return ((timeframe?.days ?? 0) + ((timeframe?.months ?? 0) * 30));
}

//UI Types
export const StyledTableRow = styled(TableRow)(({ theme }) => ({
	[`&:nth-of-type(odd)`]: {
		backgroundColor: theme.palette.action.hover
	},
	[`&:last-child td, &last-child th`]: {
		border: 0
	}
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: theme.palette.grey[400],
		color: theme.palette.common.white
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 14
	},
	[`&.${tableCellClasses.footer}`]: {
		backgroundColor: theme.palette.common.black,
		color: theme.palette.common.white
	},
}));

export const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1
})

//Constants
export const ColorHexes: string[] =
[
	"#78d3b1", "#00b1d3", "#d361d3", "#d30061", "#d3d300",
	"#78d300", "#00619b", "#7800b1", "#d31515", "#d36100",
	"#15d315", "#00009b", "#61009b", "#780000", "#9b6100"
];
